import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { fetchMyJobs, safeError, updateMyJobState } from '../data/candidateApi';

const STATES = ['Saved', 'Fit checked', 'Preparing', 'Applied'];

function stateLabel(value) {
  const normalized = String(value || 'SAVED').toUpperCase();
  if (normalized === 'FIT_CHECKED') return 'Fit checked';
  if (normalized === 'PREPARING') return 'Preparing';
  if (normalized === 'APPLIED') return 'Applied';
  return 'Saved';
}

function fitLabel(value) {
  const text = String(value || '').trim();
  return ['Strong', 'Medium', 'Weak'].includes(text) ? text : 'Fit not checked';
}

function readiness(value, label) {
  return value ? `${label} ready` : `${label} not ready`;
}

function JobCard({ job, onState }) {
  const id = job.job_result_id || job.id;
  const unavailable = Boolean(job.unavailable || job.is_unavailable || job.withdrawn_at);
  const applicationEligible = Boolean(job.application_eligible ?? fitLabel(job.qualitative_fit || job.fit) !== 'Weak');
  return (
    <article className="jmp-card-interactive p-5" data-testid={`my-job-card-${id}`}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.14em] text-[#7b694d]" data-testid={`my-job-state-${id}`}>{stateLabel(job.journey_state)}</p>
          <h2 className="mt-2 font-serif-display text-2xl font-medium" data-testid={`my-job-title-${id}`}>{job.title || job.job_title || 'Untitled role'}</h2>
          <p className="mt-1 text-sm text-[#5f6c64]" data-testid={`my-job-company-${id}`}>{job.company || 'Company not listed'} · {job.location || 'Location not listed'}</p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="jmp-chip" data-testid={`my-job-fit-${id}`}>{fitLabel(job.qualitative_fit || job.fit)}</span>
            <span className="jmp-chip" data-testid={`my-job-cv-ready-${id}`}>{readiness(job.cv_ready || job.cv_status === 'completed', 'CV')}</span>
            <span className="jmp-chip" data-testid={`my-job-letter-ready-${id}`}>{readiness(job.cover_letter_ready || job.cover_letter_status === 'completed', 'Cover letter')}</span>
            {unavailable && <span className="jmp-chip border-red-200 bg-red-50 text-red-700" data-testid={`my-job-unavailable-${id}`}>Unavailable</span>}
          </div>
          <p className="mt-4 text-sm text-[#5f6c64]" data-testid={`my-job-activity-${id}`}>{job.latest_activity || job.latest_meaningful_activity || 'No recent activity yet.'}</p>
          {stateLabel(job.journey_state) === 'Applied' && (
            <p className="mt-3 border-l-2 border-[#7b694d] pl-3 text-sm text-[#5f6c64]" data-testid={`my-job-applied-copy-${id}`}>You marked this. We don’t receive employer updates.</p>
          )}
        </div>
        <div className="flex flex-wrap gap-2 lg:justify-end">
          {job.job_url && <a href={job.job_url} target="_blank" rel="noreferrer" className="btn-secondary inline-flex items-center px-4 text-sm" data-testid={`my-job-view-link-${id}`}>View job</a>}
          {unavailable ? (
            <Link to="/find-jobs" className="btn-secondary inline-flex items-center px-4 text-sm" data-testid={`my-job-find-other-link-${id}`}>Find other jobs</Link>
          ) : applicationEligible ? (
            <Link to={`/application-package/${encodeURIComponent(id)}`} className="btn-primary inline-flex items-center px-4 text-sm" data-testid={`my-job-prepare-link-${id}`}>Prepare Application</Link>
          ) : null}
          <Button type="button" variant="outline" onClick={() => onState(id, 'APPLIED')} data-testid={`my-job-mark-applied-${id}`}>Mark applied</Button>
        </div>
      </div>
    </article>
  );
}

export default function MyJobsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');

  async function load() {
    setLoading(true);
    setError('');
    try { setItems((await fetchMyJobs()).items); } catch (err) { setError(safeError(err)); } finally { setLoading(false); }
  }

  function clearSearch() {
    setQuery('');
    setFilter('All');
  }

  useEffect(() => { load(); }, []);

  const visible = useMemo(() => items.filter((job) => {
    const text = `${job.title || job.job_title || ''} ${job.company || ''} ${job.location || ''}`.toLowerCase();
    const stateOk = filter === 'All' || stateLabel(job.journey_state) === filter;
    return stateOk && text.includes(query.toLowerCase());
  }), [items, query, filter]);

  async function handleState(id, state) {
    await updateMyJobState(id, state);
    setItems((prev) => prev.map((job) => ((job.job_result_id || job.id) === id ? { ...job, journey_state: state, applied_self_reported: state === 'APPLIED' } : job)));
  }

  return (
    <main className="min-h-screen bg-[#f6efe3] px-5 py-10 text-[#22362f]" data-testid="my-jobs-page">
      <div className="mx-auto max-w-6xl">
        <p className="text-xs uppercase tracking-[0.18em] text-[#7b694d]">Stage 3</p>
        <h1 className="mt-3 font-serif-display text-4xl font-medium sm:text-5xl" data-testid="my-jobs-title">My Jobs</h1>
        <div className="mt-8 grid gap-3 md:grid-cols-[1fr_auto]" data-testid="my-jobs-controls">
          <label className="block" htmlFor="my-jobs-search" data-testid="my-jobs-search-label">
            <span className="mb-2 block text-sm font-medium text-[#10231D]">Search your jobs</span>
            <span className="relative block">
            <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-[#7b694d]" aria-hidden="true" />
            <input id="my-jobs-search" value={query} onChange={(e) => setQuery(e.target.value)} className="min-h-[44px] w-full border border-[#8b7652] bg-white/70 pl-10 pr-3 text-sm" placeholder="Search your jobs" data-testid="my-jobs-search-input" />
            </span>
          </label>
          <label className="sr-only" htmlFor="my-jobs-state-filter">Filter by state</label>
          <select id="my-jobs-state-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="min-h-[44px] border border-[#8b7652] bg-white/70 px-3 text-sm" data-testid="my-jobs-state-filter">
            <option>All</option>{STATES.map((state) => <option key={state}>{state}</option>)}
          </select>
        </div>
        {loading && <div className="mt-6 border border-[#d8c8ab] bg-[#fff9ee] p-6" data-testid="my-jobs-loading">Loading your jobs…</div>}
        {error && <div className="mt-6 border border-red-200 bg-red-50 p-6 text-sm text-red-700" role="alert" data-testid="my-jobs-error"><p>We couldn't load your jobs right now. Try again or clear your search.</p>{error && <p className="mt-1 text-xs text-red-600">{error}</p>}<div className="mt-4 flex flex-wrap gap-3"><Button type="button" className="btn-secondary" onClick={load} data-testid="my-jobs-retry-button">Retry</Button>{(query || filter !== 'All') && <Button type="button" variant="outline" className="btn-secondary" onClick={clearSearch} data-testid="my-jobs-clear-search-button">Clear search</Button>}</div></div>}
        {!loading && !error && visible.length === 0 && (
          <section className="mt-6 border border-[#d8c8ab] bg-[#fff9ee] p-8" data-testid="my-jobs-empty-state">
            <Briefcase className="h-8 w-8 text-[#7b694d]" aria-hidden="true" />
            <h2 className="mt-4 font-serif-display text-3xl font-medium">No saved jobs yet</h2>
            <p className="mt-3 text-sm text-[#5f6c64]">Find a role, check your fit, and it will appear here.</p>
            <Link to="/find-jobs" className="mt-6 inline-flex min-h-[44px] items-center border border-[#22362f] px-5 text-sm font-medium" data-testid="my-jobs-find-link">Find Jobs</Link>
          </section>
        )}
        <div className="mt-6 space-y-4" data-testid="my-jobs-list">{visible.map((job) => <JobCard key={job.job_result_id || job.id} job={job} onState={handleState} />)}</div>
      </div>
    </main>
  );
}